import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import LocationIcon from '@/assets/icons/location.svg?react';
//import NotifActiveIcon from '@/assets/icons/notifications-active.svg?react';
import PowerSiteIcon from '@/assets/icons/power-site.svg?react';
import ReportIcon from '@/assets/icons/report.svg?react';
import RightMenu from '@/assets/icons/right-menu.svg?react';
import TechnicalMessageIcon from '@/assets/icons/technicalMessage.svg?react';
import TurbineIcon from '@/assets/icons/turbine.svg?react';
import UserIcon from '@/assets/icons/user.svg?react';
import CardError from '@/components/CardError';
import Header from '@/components/Header';
import HeaderTitle from '@/components/HeaderTitle';
import NavigationBar from '@/components/NavigationBar';
import NavigationMenu from '@/components/NavigationMenu';
import { isDetailViewRoute } from '@/constants/navigationPaths';
import DrawerContext from '@/context/DrawerContext/DrawerContext';
import { useModal } from '@/context/ModalContext';
import { useToast } from '@/context/ToastContext';
import { WebSocketProvider } from '@/context/WebSocketContext';
import useIsSignedIn from '@/hooks/useIsSignedIn';
import styles from '@/layouts/styles.module.css';
import {
  clearGlobalError,
  GENERIC_GLOBAL_ERROR_MESSAGE,
  getGlobalError,
  subscribeGlobalError
} from '@/queryProvider';
import { RootState } from '@/store';

// const drawerContentSearch = (
//   <div>
//     <a href="#">Search</a>
//   </div>
// );

function HomeLayout() {
  const userIsSignedIn = useIsSignedIn();
  const drawerCtx = useContext(DrawerContext);
  const { showToast, closeAllToasts, closeToast } = useToast();
  const { updateModalContent, closeModal } = useModal();
  const location = useLocation();
  const locationPathnameRef = useRef(location.pathname);
  locationPathnameRef.current = location.pathname;
  const locationName = useSelector(
    (state: RootState) => state.installedProducts?.locationName
  );
  const turbineId = useSelector(
    (state: RootState) => state.installedProducts?.turbineId
  );
  const turbineModel = useSelector(
    (state: RootState) => state.installedProducts?.turbineType
  );

  const drawerContentMenu = NavigationMenu();
  const isDetailView = isDetailViewRoute(location.pathname);
  const isTurbineDetail =
    location.pathname.includes('/turbines/') &&
    location.pathname !== '/turbines';
  const isAudit = location.pathname.includes('/audit');
  const isProfile = location.pathname === '/profile';

  const getHeaderLinks = () => {
    if (isProfile) {
      return [
        {
          URL: '/profile',
          component: <HeaderTitle title={'Profile'} icon={<UserIcon />} />
        }
      ];
    }
    if (isAudit) {
      return [
        {
          URL: '/audit',
          component: (
            <HeaderTitle title={'Audit Reporting'} icon={<ReportIcon />} />
          )
        }
      ];
    }
    if (!isDetailView) {
      return [
        {
          URL: '/locations',
          component: <HeaderTitle title="Locations" icon={<LocationIcon />} />
        },
        {
          URL: '/turbines',
          component: <HeaderTitle title="Turbines" icon={<TurbineIcon />} />
        }
      ];
    }

    if (isTurbineDetail) {
      return [
        {
          URL: '/turbines',
          component: (
            <HeaderTitle
              title={turbineId || 'ID'}
              subtitle={turbineModel || 'Model'}
              icon={<TurbineIcon />}
            />
          )
        }
      ];
    }

    return [
      {
        URL: '/locations',
        component: (
          <HeaderTitle
            title={locationName || 'Location'}
            subtitle="Summary"
            icon={<PowerSiteIcon />}
          />
        )
      }
    ];
  };

  const headerLinks = getHeaderLinks();

  const navigate = useNavigate();

  const [globalError, setGlobalErrorState] = useState(getGlobalError());
  const lastToastShownKeyRef = useRef<string | null>(null);
  const globalErrorToastIdRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeGlobalError(setGlobalErrorState);
    return unsubscribe;
  }, []);

  useLayoutEffect(() => {
    closeAllToasts();
    clearGlobalError();
    lastToastShownKeyRef.current = null;
    globalErrorToastIdRef.current = null;
  }, [location.pathname, location.key, closeAllToasts]);

  useEffect(() => {
    if (globalError) {
      return;
    }
    if (globalErrorToastIdRef.current) {
      closeToast(globalErrorToastIdRef.current);
      globalErrorToastIdRef.current = null;
    }
  }, [globalError, closeToast]);

  useEffect(() => {
    if (!globalError || globalError.display === 'card') {
      if (!globalError) {
        lastToastShownKeyRef.current = null;
      }
      return;
    }
    const err = globalError.error;
    const toastKey =
      err.transactionId != null
        ? String(err.transactionId)
        : `${err.endpoint ?? ''}:${err.message ?? ''}:${globalError.display}`;
    if (lastToastShownKeyRef.current === toastKey) {
      return;
    }
    lastToastShownKeyRef.current = toastKey;

    const toastMessage =
      globalError.error.message || GENERIC_GLOBAL_ERROR_MESSAGE;
    const technicalMessage = globalError.error.technicalMessage || undefined;
    closeAllToasts();
    globalErrorToastIdRef.current = showToast(toastMessage, {
      type: 'error',
      autoClose: technicalMessage ? 0 : 5000,
      technicalMessage,
      onSeeDetailsClick: technicalMessage
        ? () => {
            updateModalContent(null, {
              modalTitle: 'TechnicalMessage',
              modalIcon: TechnicalMessageIcon,
              contentDescription: technicalMessage,
              alignButtons: 'center',
              closeOnOverlayClick: true,
              variant: 'technicalMessage',
              modalButtons: [
                {
                  label: 'CLOSE',
                  variant: 'cancelBlue',
                  onClick: closeModal
                }
              ]
            });
          }
        : undefined,
      onActionClick: () => {
        clearGlobalError();
        lastToastShownKeyRef.current = null;
        globalErrorToastIdRef.current = null;
      }
    });
  }, [
    globalError,
    showToast,
    navigate,
    closeAllToasts,
    updateModalContent,
    closeModal
  ]);

  if (!userIsSignedIn) return <Navigate replace to="/login" />;

  return (
    <WebSocketProvider>
      <Header
        showLineSelector={!isDetailView && !isAudit && !isProfile}
        links={headerLinks}
        icons={[
          //   <button
          //     key="searchIcon"
          //     onClick={() => {
          //       drawerCtx.updateDrawerContent(drawerContentSearch);
          //       drawerCtx.openDrawer();
          //     }}
          //   >
          //     <NotifActiveIcon />
          //   </button>,
          <button
            key="menuIcon"
            onClick={() => {
              drawerCtx.updateDrawerContent(drawerContentMenu);
              drawerCtx.openDrawer();
            }}
          >
            <RightMenu className={styles.icon} />
          </button>
        ]}
      />
      <NavigationBar />
      <div className={styles.contentWrapper}>
        {globalError?.display === 'card' ? (
          <CardError
            errorMessage={
              globalError?.error?.message || GENERIC_GLOBAL_ERROR_MESSAGE
            }
            buttonText="TRY AGAIN"
            onClick={() => {
              clearGlobalError();
              navigate(location.pathname);
            }}
          />
        ) : (
          <Outlet />
        )}
      </div>
    </WebSocketProvider>
  );
}
export default HomeLayout;
